import { useCallback, useRef, useState } from 'react';
import { useLocale } from '@hooks/useLocale';
import { useUI } from '@context/GlobalUIContext';
import { useCartUI } from '@context/CartContext';
import useAddItem from '@adapters/bigcommerce/hooks/cart/useAddItem';
import useAddItems, {
  BatchRejectedError,
} from '@adapters/bigcommerce/hooks/cart/useAddItems';
import buildQuickAddItemBody from '@adapters/bigcommerce/hooks/cart/buildQuickAddItemBody';
import type { Cart, ItemBody } from '@adapters/bigcommerce/types/cart';
import { triggerAnalytics } from '@utils/analytics/triggerAnalytics';
import { trackAlgoliaAddToCart } from '@utils/algolia/analytics';
import { trackAddToCartEvent, GA4_EVENT_NAMES } from '@utils/gtm/ga4';
import type { TrackCartEventItem } from '@utils/gtm/ga4/trackAddToCartEvent';
import { getGenderFromString } from '@utils/gtm/ga4/utils';
import { type AtcSource, getVariantQuantity } from '@utils/cart/atcSource';
import type { BundleSelectionLine } from './useBundleSelection';

const { ADD_TO_CART } = GA4_EVENT_NAMES;

export const BUNDLE_STRIP_SOURCE = 'bundle_strip';

export type BundleAddStatus =
  | 'idle'
  | 'adding'
  | 'success'
  | 'partial'
  | 'error';

export interface BundleAddResult {
  status: BundleAddStatus;
  failed: BundleSelectionLine[];
}

export type BundleAddedUnits = Record<string, number>;

export const remainingLines = (
  lines: BundleSelectionLine[],
  addedUnits: BundleAddedUnits
): BundleSelectionLine[] =>
  lines.flatMap((line) => {
    const quantity = line.quantity - (addedUnits[line.code] ?? 0);
    if (quantity <= 0) return [];
    return [quantity === line.quantity ? line : { ...line, quantity }];
  });

interface PreparedLine {
  line: BundleSelectionLine;
  item: ItemBody;
}

const prepare = (line: BundleSelectionLine): PreparedLine | null => {
  const { product, selectedVariant, selectedColourId, selectedSizeId } = line;
  const colour = line.colours.find((c) => c.id === selectedColourId);
  const size = line.sizes.find((s) => s.id === selectedSizeId);
  const variantId = selectedVariant?.entityId;
  const { colourOptionId } = product;
  if (!variantId || !colourOptionId || !colour) return null;

  return {
    line,
    item: buildQuickAddItemBody({
      product,
      colourOptionId,
      variantId,
      colour: { id: colour.id, name: colour.name },
      size: size ? { id: size.id, name: size.name } : undefined,
      quantity: line.quantity,
    }),
  };
};

const toTrackedItem = (
  { line, item }: PreparedLine,
  atcSource: AtcSource
): TrackCartEventItem => {
  const { product, selectedVariant, colours, sizes } = line;
  return {
    brand: product.brand ? { name: product.brand.name } : null,
    gaName: product.gaName,
    price: selectedVariant?.price,
    selectedVariantSku: selectedVariant?.sku,
    productCode: product.code ?? '',
    quantity: item.quantity,
    selectedColour: {
      name: colours.find((c) => c.id === line.selectedColourId)?.name,
    },
    selectedSize: line.selectedSizeId
      ? { name: sizes.find((s) => s.id === line.selectedSizeId)?.name }
      : undefined,
    priceType: selectedVariant?.price?.priceType,
    gender: getGenderFromString(product.gaName),
    inventoryAggregated: selectedVariant?.inventoryAggregated ?? null,
    isMarketplacer: selectedVariant?.isMarketplace,
    atcSource,
  };
};

export const useAddBundleToCart = ({
  coOpBrand,
  atcSource,
}: {
  coOpBrand?: string | null;
  atcSource: AtcSource;
}) => {
  const locale = useLocale();
  const { openCartFlyout } = useUI();
  const { cart } = useCartUI();
  const cartRef = useRef(cart);
  cartRef.current = cart;

  const [status, setStatus] = useState<BundleAddStatus>('idle');
  const [failed, setFailed] = useState<BundleSelectionLine[]>([]);
  const [addedUnits, setAddedUnits] = useState<BundleAddedUnits>({});
  const addedUnitsRef = useRef(addedUnits);

  const addItems = useAddItems();
  const lastCartRef = useRef<Cart | null>(null);
  const addItem = useAddItem(({ data }) => {
    lastCartRef.current = data;
  });

  const addOneByOne = useCallback(
    async (prepared: PreparedLine[]) => {
      const units: number[] = [];
      for (const entry of prepared) {
        const before = getVariantQuantity(
          (lastCartRef.current ?? cartRef.current)?.cartData?.line_items,
          entry.item.variantId
        );
        let added = 0;
        try {
          await addItem(entry.item, atcSource);
          const after = getVariantQuantity(
            lastCartRef.current?.cartData?.line_items,
            entry.item.variantId
          );
          added = Math.min(Math.max(after - before, 0), entry.item.quantity);
        } catch (error) {
          console.error('Bundle line add failed:', error);
        }
        units.push(added);
      }
      return { units, cart: lastCartRef.current };
    },
    [addItem, atcSource]
  );

  const settle = useCallback(
    (failedLines: BundleSelectionLine[]): BundleAddResult => {
      const result: BundleAddStatus =
        failedLines.length === 0
          ? 'success'
          : Object.keys(addedUnitsRef.current).length > 0
          ? 'partial'
          : 'error';
      setStatus(result);
      setFailed(failedLines);
      return { status: result, failed: failedLines };
    },
    []
  );

  const addBundle = useCallback(
    async (lines: BundleSelectionLine[]): Promise<BundleAddResult> => {
      const pending = remainingLines(lines, addedUnitsRef.current);
      const prepared = pending
        .map(prepare)
        .filter((entry): entry is PreparedLine => entry !== null);
      if (prepared.length !== pending.length) return settle(pending);
      if (prepared.length === 0) return settle([]);
      setStatus('adding');
      setFailed([]);
      lastCartRef.current = null;

      let units: number[] = [];
      let cartData: Cart | null = null;
      try {
        cartData = await addItems(
          prepared.map(({ item }) => item),
          atcSource
        );
        units = prepared.map(({ item }) => item.quantity);
      } catch (error) {
        if (!(error instanceof BatchRejectedError)) {
          console.error('Bundle add failed:', error);
          return settle(pending);
        }
        ({ units, cart: cartData } = await addOneByOne(prepared));
      }

      const added = prepared.flatMap(({ line, item }, i) =>
        units[i] > 0 ? [{ line, item: { ...item, quantity: units[i] } }] : []
      );
      if (added.length > 0) {
        const next = { ...addedUnitsRef.current };
        added.forEach(({ line, item }) => {
          next[line.code] = (next[line.code] ?? 0) + item.quantity;
        });
        addedUnitsRef.current = next;
        setAddedUnits(next);
      }

      if (added.length > 0 && cartData) {
        triggerAnalytics({
          data: cartData,
          items: added.map(({ item }) => item),
          trackMiniCartView: true,
        });
        added.forEach(({ line, item }) =>
          trackAlgoliaAddToCart({
            variantDataForAlgolia: {
              sku: line.selectedVariant?.sku || '',
              price: line.selectedVariant?.price || null,
            },
            quantity: item.quantity,
            locale,
          })
        );
        trackAddToCartEvent({
          eventName: ADD_TO_CART,
          items: added.map((entry) => toTrackedItem(entry, atcSource)),
          extraAttributes: {
            cart_type: BUNDLE_STRIP_SOURCE,
            item_list_name: atcSource,
            coop_brand: coOpBrand,
          },
        });
        openCartFlyout();
      }

      return settle(remainingLines(lines, addedUnitsRef.current));
    },
    [
      addItems,
      addOneByOne,
      settle,
      atcSource,
      coOpBrand,
      locale,
      openCartFlyout,
    ]
  );

  const reset = useCallback(() => {
    setStatus('idle');
    setFailed([]);
    addedUnitsRef.current = {};
    setAddedUnits({});
  }, []);

  return { addBundle, status, failed, addedUnits, reset };
};

export default useAddBundleToCart;
